import type { ChakraProps } from '@chakra-ui/react';
import { Box, chakra, Image, Link, Text } from '@chakra-ui/react';

const Svg = chakra('svg');
const Circle = chakra('circle');
const Article = chakra('article');

type Props = {
  title: string;
  subTitle?: string;
  image?: string;
  href?: string;
  colorScheme?: {
    color100: string;
    color200: string;
    color300: string;
  };
};

export const NewsCard = ({ title, subTitle, image, href, colorScheme }: Props) => {
  const absoluteOverlay: ChakraProps = {
    position: 'absolute',
    w: '100%',
    borderRadius: '16px',
    left: '0',
    bottom: '0',
  };
  return (
    <Link role='group' href={href}>
      <Article
        display='flex'
        backgroundColor={colorScheme?.color100 ?? 'secondary100'}
        borderRadius='16px'
        boxShadow='4px 2px 6px rgba(61, 56, 54, 0.08);'
        boxSizing='border-box'
        color={image ? 'white' : (colorScheme?.color300 ?? 'secondary600')}
        flexDir='column'
        h='260px'
        justifyContent='flex-end'
        mr={4}
        mb={2}
        overflow='hidden'
        p={4}
        pos='relative'>
        <Box zIndex={3} textAlign='left' as='header'>
          <Text variant='smallSemibold'>{subTitle}</Text>
          <Text variant='heading3'>{title}</Text>
        </Box>
        {!image && (
          <Box {...absoluteOverlay}>
            <Svg width='272' height='228' viewBox='0 0 272 228' fill='none'>
              <Circle
                cx='257'
                cy='51'
                r='15'
                fill={colorScheme?.color200 ?? '#E58F38'}
                transition='transform 0.2s'
                transformOrigin='275px 51px'
                _groupHover={{ transform: 'scale(1.1)' }}
              />
              <Circle
                cx='83'
                cy='171'
                r='170.75'
                stroke={colorScheme?.color200 ?? '#E58F38'}
                strokeWidth='1'
                transition='transform 0.2s'
                transformOrigin='83px 171px'
                _groupHover={{ transform: 'scale(0.9)' }}
              />
            </Svg>
          </Box>
        )}
        {image && (
          <Box
            {...absoluteOverlay}
            h='100%'
            bg='linear-gradient(180deg, rgba(46, 51, 52, 0) 34.92%, rgba(45, 49, 50, 0.5) 71.11%);'
            zIndex={2}
          />
        )}
        {image && (
          <Image
            {...absoluteOverlay}
            h='100%'
            src={image}
            objectFit='cover'
            zIndex={1}
            transition='transform 0.2s'
            _groupHover={{ transform: 'scale(1.05)' }}
          />
        )}
      </Article>
    </Link>
  );
};
